/**
 * Shop-manager API client. Unlike the generic fetchJson, this keeps the error
 * CODE and the lock metadata: a 409 has to tell the operator WHO holds the
 * order, which the generic helper drops on the floor.
 */

export class ShopApiError extends Error {
  readonly code: string;
  readonly detail: string | null;
  readonly status: number;
  readonly lockedBy: string | null;
  readonly lockExpiresAt: string | null;

  constructor(
    code: string,
    detail: string | null,
    status: number,
    lockedBy: string | null = null,
    lockExpiresAt: string | null = null,
  ) {
    super([code, detail].filter((v) => v !== null && v !== '').join(' — '));
    this.name = 'ShopApiError';
    this.code = code;
    this.detail = detail;
    this.status = status;
    this.lockedBy = lockedBy;
    this.lockExpiresAt = lockExpiresAt;
  }
}

interface ShopErrorBody {
  readonly error?: string;
  readonly detail?: string;
  readonly lockedBy?: string;
  readonly lockExpiresAt?: string;
}

export async function shopJson<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    credentials: 'include',
    ...init,
    headers: { accept: 'application/json', ...init?.headers },
  });
  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as ShopErrorBody;
    throw new ShopApiError(
      body.error ?? `HTTP ${response.status}`,
      body.detail ?? null,
      response.status,
      body.lockedBy ?? null,
      body.lockExpiresAt ?? null,
    );
  }
  return (await response.json()) as T;
}

export function shopPost<T>(path: string, body?: unknown): Promise<T> {
  return shopJson<T>(path, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body ?? {}),
  });
}

export function shopPatch<T>(path: string, body: unknown): Promise<T> {
  return shopJson<T>(path, {
    method: 'PATCH',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });
}